import { useTrackingIdentity } from '~/composables/visitorIdentity';
import { nuxtLeadsRepository } from '~~/adapters/repositories/nuxt/nuxtLeadsRepository';

import { extractErrorLogContext } from '../useLogger';
import { useLeadFlowLogger } from './useLeadFlowLogger';

export interface LeadData {
	name: string;
	phone: string;
	email?: string;
	message?: string;
}

export interface PropertyLeadData extends LeadData {
	propertyId: string;
	adCampaign?: Record<string, string>;
}

export interface CondoLeadData extends LeadData {
	condoId: string;
	condoName: string;
	companyDistribution: string;
	adCampaign?: Record<string, string>;
}

export interface LeadSubmissionResult {
	success: boolean;
	error?: string;
}

export function useLeadService() {
	const logger = useLeadFlowLogger('LeadService');
	const getTrackingIdentity = useTrackingIdentity();

	async function createPropertyLead(
		data: PropertyLeadData
	): Promise<LeadSubmissionResult> {
		logger.info('Iniciando criação de lead de imóvel', {
			propertyId: data.propertyId,
			hasEmail: !!data.email,
			hasMessage: !!data.message,
			adCampaignKeys: Object.keys(data.adCampaign || {})
		});

		try {
			const [_, error] = await nuxtLeadsRepository.createPropertyLead({
				comments: data.message || '',
				propertyId: data.propertyId,
				email: data.email || null,
				name: data.name,
				phoneNumber: data.phone,
				...getTrackingIdentity(),
				...(data.adCampaign || {})
			});

			if (error) {
				logger.warn('Falha ao criar lead de imóvel (API retornou erro)', {
					propertyId: data.propertyId,
					name: data.name,
					phone: data.phone,
					errorSource: error.source,
					errorMessage: error.message,
					errorStatusCode: (
						error.originalErrorObject as { statusCode?: number }
					)?.statusCode,
					errorStatusMessage: (
						error.originalErrorObject as { statusMessage?: string }
					)?.statusMessage
				});
				return {
					success: false,
					error: error.message || 'Erro ao criar lead de imóvel'
				};
			}

			logger.info('Lead de imóvel criado com sucesso', {
				propertyId: data.propertyId
			});
			return { success: true };
		} catch (error) {
			logger.error('Erro inesperado ao criar lead de imóvel', {
				propertyId: data.propertyId,
				name: data.name,
				phone: data.phone,
				...extractErrorLogContext(error)
			});
			return {
				success: false,
				error: 'Erro inesperado ao criar lead de imóvel'
			};
		}
	}

	async function createCondoLead(
		data: CondoLeadData
	): Promise<LeadSubmissionResult> {
		logger.info('Iniciando criação de lead de condomínio', {
			condoId: data.condoId,
			condoName: data.condoName,
			companyDistribution: data.companyDistribution,
			hasEmail: !!data.email,
			hasMessage: !!data.message,
			adCampaignKeys: Object.keys(data.adCampaign || {})
		});

		try {
			const [_, error] = await nuxtLeadsRepository.createCondoLead({
				comments: data.message || '',
				canonicalCondoId: data.condoId,
				companyDistribution: data.companyDistribution,
				canonicalCondoName: data.condoName,
				email: data.email || null,
				name: data.name,
				phoneNumber: data.phone,
				...getTrackingIdentity(),
				...(data.adCampaign || {})
			});

			if (error) {
				logger.warn('Falha ao criar lead de condomínio (API retornou erro)', {
					condoId: data.condoId,
					condoName: data.condoName,
					name: data.name,
					phone: data.phone,
					errorSource: error.source,
					errorMessage: error.message,
					errorStatusCode: (
						error.originalErrorObject as { statusCode?: number }
					)?.statusCode,
					errorStatusMessage: (
						error.originalErrorObject as { statusMessage?: string }
					)?.statusMessage
				});
				return {
					success: false,
					error: error.message || 'Erro ao criar lead de condomínio'
				};
			}

			logger.info('Lead de condomínio criado com sucesso', {
				condoId: data.condoId,
				condoName: data.condoName
			});
			return { success: true };
		} catch (error) {
			logger.error('Erro inesperado ao criar lead de condomínio', {
				condoId: data.condoId,
				condoName: data.condoName,
				name: data.name,
				phone: data.phone,
				...extractErrorLogContext(error)
			});
			return {
				success: false,
				error: 'Erro inesperado ao criar lead de condomínio'
			};
		}
	}

	return {
		createPropertyLead,
		createCondoLead
	};
}
